import { Component, Vue, Watch, toNative } from 'vue-facing-decorator';
import { Teleport, h, nextTick, reactive, type VNode } from 'vue';
import { SupportChatCodeVerifyStatus } from '../../api/public/enums/supportChatCodeVerifyStatus';
import { SupportChatEscalationStatus } from '../../api/public/enums/supportChatEscalationStatus';
import { SupportChatEventMatch } from '../../api/public/enums/supportChatEventMatch';
import { SupportChatEventStatus } from '../../api/public/enums/supportChatEventStatus';
import { SupportChatLookupStatus } from '../../api/public/enums/supportChatLookupStatus';
import { SupportChatOrderSummaryStatus } from '../../api/public/enums/supportChatOrderSummaryStatus';
import { SupportChatResendStatus } from '../../api/public/enums/supportChatResendStatus';
import { SupportChatAnalytics } from '../analytics';
import { SupportChatApiError, createApi, toTranscriptWire, type ISupportChatApi } from '../api/client';
import { readConfig, type SupportChatConfig } from '../config';
import { FlowEngine, HUMAN_STEP_ID, createInitialState, type EngineEvent } from '../flow/engine';
import { looksLikeEventName, supportFlows } from '../flow/flows';
import type { BackendActionName, ConversationState, Prompt, Resolution, StepOption } from '../flow/types';
import { createTranslator, resolveLanguage } from '../i18n';
import { loadState, saveState } from '../persistence';
import { TranscriptSync } from '../transcript-sync';

/**
 * Support chat widget: floating launcher + chat panel.
 *
 * This is a standalone mini-app mounted into the public Razor pages
 * (NewHome layout, shopping cart). It deliberately does NOT use PowerDuck —
 * those pages don't load the SPA CSS chain and the widget has to stay small.
 * All customer text is rendered as text nodes (never v-html).
 */
@Component
class SupportChatWidget extends Vue {
	config: SupportChatConfig = readConfig();
	state: ConversationState = null as unknown as ConversationState;
	engine: FlowEngine = null as unknown as FlowEngine;
	api: ISupportChatApi = null as unknown as ISupportChatApi;
	analytics: SupportChatAnalytics = null as unknown as SupportChatAnalytics;
	archive: TranscriptSync = null as unknown as TranscriptSync;
	t: (key: string) => string = (k) => k;

	inputValue = '';
	inputError: string | null = null;
	formValues: Record<string, string> = {};
	formErrors: Record<string, string> = {};
	busy = false;
	/** Proactive greeting bubble above the launcher (shown once per session). */
	teaserVisible = false;
	/** Exit animation plays before the teaser unmounts. */
	teaserClosing = false;
	private teaserTimer: number | null = null;
	/** Owner request: the teaser folds back into the launcher on its own after TEASER_AUTO_HIDE_MS. */
	private teaserHideTimer: number | null = null;
	/** Ticket forms the page marked with [data-isc-avoid]: the launcher never floats over their controls. */
	private avoidZones: HTMLElement[] = [];
	/** One of them is on screen… */
	avoidVisible = false;
	/** …and at this viewport width its controls reach under the launcher's corner. */
	avoidCollides = false;
	/** Horizontal extent of the launcher (+ gap), kept while the launcher is unmounted behind the open panel. */
	private launcherSpan: { left: number; right: number } | null = null;
	private avoidObserver: IntersectionObserver | null = null;
	private avoidMutations: MutationObserver | null = null;
	private avoidFrame: number | null = null;
	/** Page slots ([data-isc-help]) that get the inline "need help?" link standing in for the yielding launcher. */
	helpSlots: HTMLElement[] = [];
	/** Panel plays its exit animation before unmounting. */
	closing = false;
	/**
	 * Assistant replies are revealed one by one with a short "typing" pause so the
	 * conversation reads like a chat, not a form dump. `revealedCount` is how many
	 * transcript entries are currently visible.
	 */
	revealedCount = 0;
	revealing = false;
	private revealTimer: number | null = null;

	// ------------------------------------------------------------ lifecycle

	created() {
		const lang = resolveLanguage(this.config.language);
		this.t = createTranslator(lang);
		this.api = createApi(this.config.apiBase);
		this.analytics = new SupportChatAnalytics(this.api);
		this.archive = new TranscriptSync(this.api);

		const restored = loadState(Date.now(), lang);
		const initial = restored ?? createInitialState(this.config.eventId, this.config.eventName, Date.now(), lang);
		// event context always comes from the current page
		initial.contextEventId = this.config.eventId;
		initial.contextEventName = this.config.eventName;
		this.state = reactive(initial) as ConversationState;
		// a restored conversation is shown as-is, no replay
		this.revealedCount = this.state.transcript.length;
		this.analytics.setConversation(this.state.conversationId);
		// a restored tab re-sends its visible transcript; the server keys bubbles by position, so nothing duplicates
		this.archive.setConversation(this.state.conversationId);
		this.archive.update(this.state.transcript);

		this.engine = new FlowEngine(this.state, {
			flows: supportFlows,
			t: this.t,
			vars: () => ({
				supportEmail: this.config.supportEmail,
				supportPhone: this.config.supportPhone ?? '',
				matchedEventName: String(this.state.answers.matchedEventName ?? ''),
				matchedEventDate: String(this.state.answers.matchedEventDate ?? ''),
				// availability answers (public event data - the same the event page shows)
				eventUrl: this.absoluteEventUrl(),
				nextDate: String(this.state.answers.nextDate ?? ''),
				saleStart: String(this.state.answers.saleStart ?? ''),
				otherDate: String(this.state.answers.otherDate ?? ''),
				// organizer phone, when the event has one - appended to the organizer answers as its own line
				organizerPhoneLine: this.state.answers.organizerPhone ? '\n' + this.t('common.phoneLine').replace('{phone}', String(this.state.answers.organizerPhone)) : '',
			}),
		});
		this.engine.on((e) => this.onEngineEvent(e));
		if (restored?.pendingAction) {
			// a page reload interrupted an action — put the customer back before it
			this.engine.back();
		}
	}

	mounted() {
		document.addEventListener('keydown', this.onKeydown);
		this.helpSlots = Array.from(document.querySelectorAll<HTMLElement>(HELP_SLOT_SELECTOR));
		this.watchAvoidZones();
		if (this.state.isOpen) {
			void this.afterOpen(false);
		} else if (!this.hasConversation && !isTeaserDismissed()) {
			this.teaserTimer = window.setTimeout(() => {
				// never over a ticket form, and not on small screens, where the bubble takes half the viewport
				if (!this.state.isOpen && !this.avoidVisible && !isCompactViewport()) {
					this.teaserVisible = true;
					this.analytics.track('teaser_shown', { props: { hasEvent: this.config.eventId != null } });
					this.scheduleTeaserHide();
				}
			}, TEASER_DELAY_MS);
		}
	}

	beforeUnmount() {
		document.removeEventListener('keydown', this.onKeydown);
		this.avoidObserver?.disconnect();
		this.avoidMutations?.disconnect();
		window.removeEventListener('resize', this.scheduleMeasureAvoid);
		if (this.avoidFrame != null) {
			cancelAnimationFrame(this.avoidFrame);
		}
		if (this.teaserTimer != null) {
			clearTimeout(this.teaserTimer);
		}
		this.cancelTeaserHide();
		if (this.revealTimer != null) {
			clearTimeout(this.revealTimer);
		}
		this.analytics.flush();
	}

	// ------------------------------------------------------- message reveal

	@Watch('state.transcript.length')
	onTranscriptLength(len: number) {
		this.archive.update(this.state.transcript);
		if (len < this.revealedCount) {
			// back / restart / retry truncated the transcript
			this.revealedCount = len;
			if (this.revealTimer != null) {
				clearTimeout(this.revealTimer);
				this.revealTimer = null;
				this.revealing = false;
			}
		}
		this.scheduleReveal();
	}

	scheduleReveal() {
		if (this.revealTimer != null) {
			return;
		}
		const t = this.state.transcript;
		if (this.revealedCount >= t.length) {
			this.revealing = false;
			return;
		}
		const entry = t[this.revealedCount];
		// the customer's own words, error bubbles and the greeting behind the hero appear instantly
		const isHeroGreeting = this.revealedCount === 0 && this.state.stepId === 'main';
		if (entry.role === 'customer' || entry.kind === 'error' || isHeroGreeting || prefersReducedMotion()) {
			this.revealedCount++;
			void this.scrollToBottom();
			this.scheduleReveal();
			return;
		}
		this.revealing = true;
		void this.scrollToBottom();
		const delay = Math.min(REVEAL_MAX_MS, REVEAL_BASE_MS + entry.text.length * REVEAL_PER_CHAR_MS);
		this.revealTimer = window.setTimeout(() => {
			this.revealTimer = null;
			this.revealedCount++;
			this.revealing = false;
			void this.scrollToBottom();
			if (this.revealedCount < this.state.transcript.length) {
				// reading pause before the next bubble starts "typing"
				this.revealTimer = window.setTimeout(() => {
					this.revealTimer = null;
					this.scheduleReveal();
				}, REVEAL_GAP_MS);
				return;
			}
			this.scheduleReveal();
			if (this.promptReady) {
				this.focusInput();
			}
		}, delay);
	}

	/** Composer is shown only after every assistant reply has been "typed". */
	get promptReady(): boolean {
		return !this.busy && !this.revealing && this.revealedCount >= this.state.transcript.length;
	}

	get visibleTranscript() {
		return this.state.transcript.slice(0, this.revealedCount);
	}

	/** Fresh conversation at the main menu → friendly hero instead of a plain bubble. */
	get showHero(): boolean {
		return this.state.stepId === 'main' && this.state.transcript.length === 1 && !this.busy;
	}

	/** Main menu (big cards) lives in the scrollable message area, not in the composer. */
	get isMainMenu(): boolean {
		return this.state.stepId === 'main' && this.promptReady && this.prompt.mode === 'choice';
	}

	/**
	 * Main menu. Nine full-width rows were a wall of choices (owner, 2026-10-15), so only the
	 * five topics people actually arrive with stay visible and the rest fold behind one toggle.
	 * Which flow sits where is a property of the flow (`secondary`), not of this component.
	 */
	/** "Ďalšie možnosti" expanded? Resets with the conversation. */
	moreOpen = false;

	renderMainMenu() {
		const p = this.prompt;
		if (p.mode !== 'choice') {
			return null;
		}
		const primary = p.options.filter((o) => o.group !== 'more');
		const more = p.options.filter((o) => o.group === 'more');
		const itemWithDelay = (o: StepOption, style: Record<string, string>) => (
			<button key={o.id} type="button" class="isc-menu__item isc-rise" style={style} onClick={() => this.choose(o)}>
				<span class="isc-menu__icon" aria-hidden="true">
					{o.icon}
				</span>
				<span class="isc-menu__label">{this.engine.text(o.labelKey)}</span>
				<span class="isc-menu__chevron" aria-hidden="true">
					{chevronIcon()}
				</span>
			</button>
		);
		return (
			<div class="isc-menu" role="group">
				{primary.map((o, i) => itemWithDelay(o, stagger(i)))}
				{more.length > 0 && (
					<button
						type="button"
						class={['isc-menu__more', this.moreOpen ? 'isc-menu__more--open' : '']}
						aria-expanded={this.moreOpen ? 'true' : 'false'}
						onClick={() => {
							this.moreOpen = !this.moreOpen;
							void this.scrollToBottom();
						}}
					>
						<span class="isc-menu__more-label">
							{this.t(this.moreOpen ? 'main.lessOptions' : 'main.moreOptions')}
							{!this.moreOpen && (
								// the folded topics stay readable, so nobody has to guess what is under here
								<span class="isc-menu__more-hint">
									{more.map((o) => this.t(o.shortLabelKey ?? o.labelKey)).join(' · ')}
								</span>
							)}
						</span>
						<span class="isc-menu__more-chevron" aria-hidden="true">
							{chevronIcon()}
						</span>
					</button>
				)}
				{/* a reveal the customer just asked for must feel instant: its own short stagger,
				    not the tail of the six cards above it (otherwise the last row lands ~0.6 s late) */}
				{this.moreOpen && more.map((o, i) => itemWithDelay(o, stagger(i, EXPAND_STAGGER_MS)))}
			</div>
		);
	}

	/** A flow was started and not finished — the launcher offers to continue. */
	get hasConversation(): boolean {
		return !!this.state.flowId && !this.engine.isFinished;
	}

	get teaserFlows() {
		const flows = this.engine.menuFlows;
		const top = flows.filter((f) => f.id !== 'other').slice(0, 2);
		const other = flows.find((f) => f.id === 'other');
		return other ? [...top, other] : top;
	}

	/** The launcher is hidden while it would sit on a visible ticket form's controls. */
	get launcherYields() {
		return this.avoidVisible && this.avoidCollides;
	}

	watchAvoidZones() {
		this.avoidZones = Array.from(document.querySelectorAll<HTMLElement>(AVOID_SELECTOR));
		if (this.avoidZones.length === 0 || typeof IntersectionObserver === 'undefined') {
			return;
		}

		const visible = new Set<Element>();
		this.avoidObserver = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					visible.add(entry.target);
				} else {
					visible.delete(entry.target);
				}
			}
			this.avoidVisible = visible.size > 0;
			this.measureAvoid();
		});
		// the form re-renders between its steps (picker, order details, payment)
		this.avoidMutations = new MutationObserver(() => this.scheduleMeasureAvoid());
		for (const zone of this.avoidZones) {
			this.avoidObserver.observe(zone);
			this.avoidMutations.observe(zone, { childList: true, subtree: true });
		}
		window.addEventListener('resize', this.scheduleMeasureAvoid);
		this.measureAvoid();
	}

	scheduleMeasureAvoid() {
		if (this.avoidFrame != null) {
			return;
		}
		this.avoidFrame = requestAnimationFrame(() => {
			this.avoidFrame = null;
			this.measureAvoid();
		});
	}

	/** Does any control of a marked form reach into the launcher's column (left on phones, right elsewhere)? */
	measureAvoid() {
		const launcher = this.$refs.launcher as HTMLElement | undefined;
		if (launcher) {
			const r = launcher.getBoundingClientRect();
			this.launcherSpan = { left: r.left - AVOID_GAP_PX, right: r.right + AVOID_GAP_PX };
		}
		const span = this.launcherSpan;
		if (!span) {
			return;
		}
		this.avoidCollides = this.avoidZones.some((zone) =>
			Array.from(zone.querySelectorAll<HTMLElement>(AVOID_CONTROLS)).some((el) => {
				const r = el.getBoundingClientRect();
				return r.width > 0 && r.left < span.right && r.right > span.left;
			}),
		);
	}

	dismissTeaser(track = true) {
		this.cancelTeaserHide();
		this.teaserVisible = false;
		this.teaserClosing = false;
		markTeaserDismissed();
		if (track) {
			this.analytics.track('teaser_dismissed');
		}
	}

	/** Starts (or restarts) the countdown after which the teaser folds back into the launcher by itself. */
	scheduleTeaserHide() {
		this.cancelTeaserHide();
		this.teaserHideTimer = window.setTimeout(() => {
			this.teaserHideTimer = null;
			if (!this.teaserVisible || this.state.isOpen) {
				return;
			}
			this.analytics.track('teaser_autohidden');
			if (prefersReducedMotion()) {
				this.dismissTeaser(false);
				return;
			}
			// play the exit animation, then unmount
			this.teaserClosing = true;
			this.teaserHideTimer = window.setTimeout(() => {
				this.teaserHideTimer = null;
				this.dismissTeaser(false);
			}, TEASER_HIDE_ANIM_MS);
		}, TEASER_AUTO_HIDE_MS);
	}

	cancelTeaserHide() {
		if (this.teaserHideTimer != null) {
			clearTimeout(this.teaserHideTimer);
			this.teaserHideTimer = null;
		}
	}

	/** While the customer is reading (pointer over the bubble) the countdown pauses; it restarts when they leave. */
	onTeaserEnter() {
		if (this.teaserClosing) {
			return;
		}
		this.cancelTeaserHide();
	}

	onTeaserLeave() {
		if (this.teaserClosing || !this.teaserVisible) {
			return;
		}
		this.scheduleTeaserHide();
	}

	async teaserChoose(flowId: string) {
		this.analytics.track('teaser_clicked', { props: { flowId } });
		this.dismissTeaser(false);
		await this.open();
		this.engine.choose(flowId);
		this.persist();
		this.focusInput();
	}

	// -------------------------------------------------------------- helpers

	get prompt(): Prompt {
		return this.engine.prompt();
	}

	get transcript() {
		return this.state.transcript;
	}

	persist() {
		saveState(this.state);
	}

	onKeydown(e: KeyboardEvent) {
		if (e.key === 'Escape' && this.state.isOpen) {
			this.close(true);
		}
	}

	onEngineEvent(e: EngineEvent) {
		switch (e.type) {
			case 'flow_started':
				this.analytics.track('flow_started', { flowId: e.flowId, props: { intent: e.intent } });
				this.sendOutcome(e.flowId, null);
				break;
			case 'step_answered':
				this.analytics.track('step_answered', { flowId: e.flowId, stepId: e.stepId, props: { optionId: e.optionId ?? null } });
				if (e.optionId === HUMAN_STEP_ID) {
					this.analytics.track('human_requested', { flowId: e.flowId, stepId: e.stepId });
				}
				break;
			case 'action_requested':
				void this.runAction(e.action);
				break;
			case 'action_result':
				this.analytics.track('action_result', { props: { action: e.action, status: e.status } });
				if (e.action === 'lookup' || e.action === 'lookupFallback') {
					this.analytics.track(e.status === SupportChatLookupStatus.NotFound ? 'order_not_found' : 'order_found', {
						props: { action: e.action },
					});
				}
				if (e.action === 'resend') {
					this.analytics.track(e.status === SupportChatResendStatus.Sent || e.status === SupportChatResendStatus.AlreadySent ? 'ticket_resend_success' : 'ticket_resend_failed');
				}
				if (e.action === 'organizerContact' && e.status === SupportChatEventStatus.Found) {
					this.analytics.track('organizer_redirect');
				}
				if (e.action === 'escalate' && (e.status === SupportChatEscalationStatus.Created || e.status === SupportChatEscalationStatus.Duplicate)) {
					this.analytics.track('support_request_created');
				}
				break;
			case 'action_failed':
				this.analytics.track('action_failed', { props: { action: e.action } });
				break;
			case 'flow_ended':
				this.analytics.track(e.resolution === 'resolved' ? 'flow_resolved' : e.resolution === 'escalated' ? 'flow_escalated' : 'flow_ended', {
					flowId: e.flowId,
					props: { resolution: e.resolution },
				});
				this.sendOutcome(e.flowId, e.resolution);
				break;
			case 'back':
				this.analytics.track('back', { stepId: e.toStepId });
				break;
			case 'restart':
				this.analytics.track('restart');
				break;
		}
		this.persist();
		void this.scrollToBottom();
	}

	async scrollToBottom() {
		await nextTick();
		const el = this.$refs.messages as HTMLElement | undefined;
		if (el) {
			el.scrollTop = this.showHero ? 0 : el.scrollHeight;
		}
	}

	/** Focus text inputs only — programmatic focus on buttons draws a focus ring for mouse users. */
	focusInput() {
		void nextTick(() => {
			const el = this.$refs.input as HTMLInputElement | HTMLTextAreaElement | undefined;
			el?.focus?.();
		});
	}

	// ------------------------------------------------------------- actions

	async open() {
		if (this.teaserVisible) {
			this.dismissTeaser(false);
		}
		this.state.isOpen = true;
		await this.afterOpen(true);
	}

	async afterOpen(track: boolean) {
		if (track) {
			this.analytics.track('chatbot_opened', { props: { hasEvent: this.config.eventId != null } });
		}
		this.engine.start();
		this.persist();
		await this.ensureConversation();
		await this.scrollToBottom();
		this.focusInput();
	}

	/** @param fromKeyboard return focus to the launcher only for keyboard users (mouse users would see a focus ring). */
	close(fromKeyboard = false) {
		if (this.closing) {
			return;
		}
		this.analytics.track('chatbot_closed', { stepId: this.state.stepId, props: { finished: this.engine.isFinished } });
		this.analytics.flush();
		const finish = () => {
			this.closing = false;
			this.state.isOpen = false;
			this.persist();
			// focus goes back to the launcher (QA: it was left on <body>), for mouse users too
			void nextTick(() => {
				(this.$refs.launcher as HTMLButtonElement | undefined)?.focus({ preventScroll: !fromKeyboard });
				// the launcher is back in the DOM: the viewport may have changed while the panel covered it
				this.measureAvoid();
			});
		};
		if (prefersReducedMotion()) {
			finish();
			return;
		}
		this.closing = true;
		window.setTimeout(finish, PANEL_CLOSE_MS);
	}

	/** "Done, thanks": the conversation is over – reset to the greeting and close, so the next open starts fresh. */
	finish() {
		this.restart();
		this.close();
	}

	/** Topic / outcome onto the conversation row (owner decision 2026-10-20). Beacon: never awaited by the caller, never surfaced. */
	private sendOutcome(flowId: string | null, resolution: string | null) {
		if (!flowId && !resolution) {
			return;
		}
		void this.sendOutcomeAsync(flowId, resolution);
	}

	private async sendOutcomeAsync(flowId: string | null, resolution: string | null) {
		// after "done" the next conversation row is created here, by the next topic - not by the restart itself
		await this.ensureConversation();
		const conversationId = this.state.conversationId;
		if (!conversationId) {
			return;
		}
		await this.api.outcome({ ConversationId: conversationId, FlowId: flowId, Resolution: resolution });
	}

	async ensureConversation() {
		if (this.state.conversationId) {
			return;
		}
		try {
			const res = await this.api.start({ Language: this.config.language, EventId: this.config.eventId, PageUrl: location.pathname });
			this.state.conversationId = res.ConversationId;
			this.analytics.setConversation(res.ConversationId);
			this.archive.setConversation(res.ConversationId);
			this.persist();
		} catch {
			// backend not reachable yet — the guided part still works, actions will retry
		}
	}

	choose(option: StepOption) {
		if (this.busy) {
			return;
		}
		this.inputError = null;
		this.engine.choose(option.id);
		this.focusInput();
	}

	chooseHuman() {
		if (this.busy) {
			return;
		}
		this.engine.choose(HUMAN_STEP_ID);
		this.focusInput();
	}

	submitText() {
		if (this.busy) {
			return;
		}
		const err = this.engine.submitText(this.inputValue);
		if (err) {
			this.inputError = this.t(err);
			return;
		}
		this.inputValue = '';
		this.inputError = null;
		this.moreOpen = false;
		this.focusInput();
	}

	skipText() {
		if (this.busy) {
			return;
		}
		this.inputValue = '';
		this.submitText();
	}

	submitForm() {
		if (this.busy) {
			return;
		}
		const errors = this.engine.submitForm(this.formValues);
		if (errors) {
			this.formErrors = Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, this.t(v)]));
			return;
		}
		this.formValues = {};
		this.formErrors = {};
		this.focusInput();
	}

	confirm() {
		if (this.busy) {
			return;
		}
		this.engine.confirm();
	}

	back() {
		if (this.busy) {
			return;
		}
		this.inputError = null;
		// the step we return to gets its own answer back for editing - never the draft typed in the step we leave
		this.inputValue = this.engine.back() ?? '';
		const step = this.engine.currentStep;
		const saved = step?.type === 'form' && step.field ? this.state.answers[step.field] : null;
		this.formValues = saved && typeof saved === 'object' ? { ...(saved as Record<string, string>) } : {};
		this.focusInput();
	}

	restart() {
		if (this.busy) {
			return;
		}
		if (!this.engine.isFinished && this.state.flowId) {
			this.analytics.track('flow_abandoned', { flowId: this.state.flowId, stepId: this.state.stepId });
		}
		this.inputValue = '';
		this.inputError = null;
		// the finished conversation keeps its tail; then both syncs let go of it, or the next flush
		// would send the new greeting as "truncate from 0" into the OLD conversation's archive
		this.archive.flush();
		this.archive.setConversation(null);
		this.analytics.flush();
		this.analytics.setConversation(null);
		this.engine.restart();
		// no conversation row yet: it would stay "no topic, unfinished" whenever the customer just leaves;
		// the next topic (sendOutcome) or action (runAction) creates it
		this.focusInput();
	}

	retry() {
		if (this.busy) {
			return;
		}
		this.engine.retryAction();
	}

	/**
	 * The server answers with a relative event path (/e-123/slug); the customer gets the whole
	 * address, so the link also works when they copy the sentence out of the chat.
	 */
	absoluteEventUrl(): string {
		const path = String(this.state.answers.eventUrl ?? '');
		if (!path) {
			return '';
		}
		if (/^https?:\/\//i.test(path)) {
			return path;
		}
		return path.startsWith('/') && typeof location !== 'undefined' ? location.origin + path : path;
	}

	async runAction(action: BackendActionName, retried = false) {
		this.busy = true;
		await this.ensureConversation();
		const s = this.state;
		const a = s.answers;
		const conversationId = s.conversationId ?? '';
		try {
			switch (action) {
				case 'lookup':
				case 'lookupFallback': {
					this.analytics.track('order_lookup_started', { props: { fallback: action === 'lookupFallback' } });
					const req = {
						ConversationId: conversationId,
						// page event is only a hint until the customer confirmed / denied it
						EventId: a.eventId !== undefined ? (a.eventId as number | null) : this.config.eventId,
						EventName: (a.eventName as string | null) ?? null,
						OrderEmail: String(a.orderEmail ?? ''),
						PurchaseRange: (a.purchaseRange as string | null) ?? null,
						Purpose: (s.flowId === 'ticket-not-received' ? 'resend' : 'verify') as 'resend' | 'verify',
					};
					const res = action === 'lookup' ? await this.api.lookup(req) : await this.api.lookupFallback(req);
					this.engine.applyActionResult(res.Status, res.ActionToken ? { actionToken: res.ActionToken, orderCount: res.OrderCount ?? 1 } : undefined);
					break;
				}
				case 'orderStatus': {
					this.analytics.track('order_status_requested');
					const res = await this.api.orderStatus({
						ConversationId: conversationId,
						EventId: a.eventId !== undefined ? (a.eventId as number | null) : this.config.eventId,
						EventName: (a.eventName as string | null) ?? null,
						OrderEmail: String(a.orderEmail ?? ''),
						PurchaseRange: null,
						Purpose: 'verify',
					});
					// status word only (house rule: the browser never learns order data); the token,
					// when the server issued one, lets the flow offer the resend straight away
					this.engine.applyActionResult(res.Status, {
						actionToken: res.ActionToken ?? undefined,
						orderCount: res.ResendOrderCount ?? 0,
					});
					break;
				}
				case 'availability': {
					this.analytics.track('availability_requested');
					const res = await this.api.availability({
						ConversationId: conversationId,
						EventId: a.eventId !== undefined ? (a.eventId as number | null) : this.config.eventId,
						EventName: (a.eventName as string | null) ?? null,
					});
					this.engine.applyActionResult(res.Status, {
						// the server's own name of the event the answer is about - better than the customer's typo
						eventName: res.EventName || (a.eventName as string | null) || null,
						eventUrl: res.EventUrl ?? '',
						nextDate: res.NextDate ?? '',
						saleStart: res.SaleStart ?? '',
						otherDate: res.OtherDate ?? '',
						seatSelection: !!res.SeatSelection,
					});
					break;
				}
				case 'sendCode': {
					const res = await this.api.sendCode({ ConversationId: conversationId, OrderEmail: String(a.orderEmail ?? ''), Code: null });
					this.engine.applyActionResult(res.Status);
					break;
				}
				case 'verifyCode': {
					const res = await this.api.verifyCode({ ConversationId: conversationId, OrderEmail: String(a.orderEmail ?? ''), Code: String(a.emailCode ?? '') });
					delete a.emailCode;
					this.engine.applyActionResult(res.Status, res.Status === SupportChatCodeVerifyStatus.Verified ? { verifiedEmail: a.orderEmail } : undefined);
					break;
				}
				case 'resend': {
					this.analytics.track('ticket_resend_requested');
					const token = String(a.actionToken ?? '');
					if (!token) {
						this.engine.applyActionResult(SupportChatResendStatus.Failed);
						break;
					}
					const res = await this.api.resend({ ConversationId: conversationId, ActionToken: token });
					delete a.actionToken;
					this.engine.applyActionResult(res.Status);
					break;
				}
				case 'resolveEvent': {
					const res = await this.api.resolveEvent({
						ConversationId: conversationId,
						EventId: (a.eventId as number | null) ?? null,
						EventName: (a.eventName as string | null) ?? null,
					});
					// a question typed into the event-name field must not come back as "the event 'Can you help me…'" (report 2026-10-30)
					const typed = String(a.eventName ?? '');
					const notAName = res.Status !== SupportChatEventStatus.Found && !looksLikeEventName(typed);
					this.engine.applyActionResult(
						res.Status,
						res.Status === SupportChatEventStatus.Found
							? { eventId: res.EventId ?? null, matchedEventName: res.EventName ?? '', matchedEventDate: res.EventDate ?? '', eventMatch: res.Match ?? SupportChatEventMatch.Exact }
							: { matchedEventName: null, matchedEventDate: null, eventMatch: null, ...(notAName ? { eventName: '', description: a.description ? a.description : typed } : {}) },
					);
					break;
				}
				case 'organizerContact': {
					const res = await this.api.organizerContact({
						ConversationId: conversationId,
						EventId: (a.eventId as number | null) ?? null,
						EventName: (a.eventName as string | null) ?? null,
					});
					// the event page link rides along: the event-question answer points there for dates and prices
					this.engine.applyActionResult(res.Status, res.OrganizerEmail ? { organizerEmail: res.OrganizerEmail, organizerPhone: res.OrganizerPhone ?? null, eventUrl: res.EventUrl ?? '' } : undefined);
					break;
				}
				case 'eventInvoicing': {
					const res = await this.api.eventInvoicing({
						ConversationId: conversationId,
						EventId: (a.eventId as number | null) ?? null,
						EventName: (a.eventName as string | null) ?? null,
					});
					this.engine.applyActionResult(res.Status, { invoicing: res.Status });
					break;
				}
				case 'escalate': {
					const flow = this.engine.currentFlow;
					const step = this.engine.currentStep;
					if (!a.escalationKey) {
						a.escalationKey = randomKey();
					}
					const res = await this.api.escalate({
						ConversationId: conversationId,
						Intent: flow?.intent ?? 'unknown',
						Reason: step?.escalationReason ?? flow?.intent ?? 'unknown',
						ContactEmail: String(a.contactEmail ?? a.orderEmail ?? ''),
						AnswersJson: JSON.stringify(stripInternal(a)),
						Transcript: toTranscriptWire(s.transcript),
						Language: this.config.language,
						IdempotencyKey: String(a.escalationKey),
					});
					this.engine.applyActionResult(res.Status);
					break;
				}
			}
		} catch (e) {
			if (e instanceof SupportChatApiError && e.code === 'conversation_expired' && !retried) {
				// the server forgot us (24 h limit or restored tab) → new conversation, same action, once
				s.conversationId = null;
				// a proven mailbox belongs to the old conversation
				delete s.answers.verifiedEmail;
				this.analytics.setConversation(null);
				this.archive.setConversation(null);
				return await this.runAction(action, true);
			}
			if (e instanceof SupportChatApiError && e.code === 'rate_limited') {
				this.engine.failAction();
				const last = s.transcript[s.transcript.length - 1];
				if (last && last.kind === 'error') {
					last.text = this.t('error.rateLimited');
				}
			} else {
				this.engine.failAction();
			}
		} finally {
			this.busy = false;
			this.persist();
			void this.scrollToBottom();
			this.focusInput();
		}
	}

	// -------------------------------------------------------------- render

	render() {
		const s = this.state;
		return (
			<div class={['isc-root', s.isOpen ? 'isc-root--open' : '', this.launcherYields ? 'isc-root--avoid' : '']}>
				{!s.isOpen && this.teaserVisible && this.renderTeaser()}
				{!s.isOpen && this.renderLauncher()}
				{s.isOpen && this.renderPanel()}
				{/* h(): the project's JSX typings only accept class components */}
				{this.avoidCollides && this.helpSlots.map((slot, i) => h(Teleport, { key: i, to: slot }, [this.renderInlineHelp()]))}
			</div>
		);
	}

	/** Opens the chat from below a ticket form, where the launcher yields. */
	renderInlineHelp() {
		return (
			<button type="button" class="isc-inline-help" aria-haspopup="dialog" onClick={() => void this.open()}>
				{botIcon()}
				<span>{this.t(this.hasConversation ? 'launcher.resume' : 'launcher.title')}</span>
			</button>
		);
	}

	renderLauncher() {
		const resume = this.hasConversation;
		return (
			<button
				ref="launcher"
				type="button"
				class={['isc-launcher', resume ? 'isc-launcher--resume' : '', this.teaserVisible ? 'isc-launcher--teaser' : '']}
				aria-label={this.t('widget.launcherLabel')}
				aria-haspopup="dialog"
				aria-expanded="false"
				onClick={() => void this.open()}
			>
				<span class="isc-launcher__avatar" aria-hidden="true">
					{botIcon()}
					<span class="isc-launcher__dot" />
				</span>
				<span class="isc-launcher__text">
					<span class="isc-launcher__title">{this.t(resume ? 'launcher.resume' : 'launcher.title')}</span>
					<span class="isc-launcher__subtitle">{this.t(resume ? 'launcher.resumeSubtitle' : 'launcher.subtitle')}</span>
				</span>
				{resume && <span class="isc-launcher__badge" aria-hidden="true">1</span>}
			</button>
		);
	}

	renderTeaser() {
		const hasEvent = !!this.config.eventName;
		return (
			<div
				class={['isc-teaser', this.teaserClosing ? 'isc-teaser--closing' : '']}
				role="region"
				aria-label={this.t('widget.title')}
				onMouseenter={() => this.onTeaserEnter()}
				onMouseleave={() => this.onTeaserLeave()}
				onFocusin={() => this.onTeaserEnter()}
				onFocusout={() => this.onTeaserLeave()}
			>
				<button type="button" class="isc-teaser__close" aria-label={this.t('teaser.dismiss')} onClick={() => this.dismissTeaser()}>
					{closeIcon()}
				</button>
				<div class="isc-teaser__head">
					<span class="isc-teaser__avatar" aria-hidden="true">
						{botIcon()}
					</span>
					<div>
						<div class="isc-teaser__title">{this.t('teaser.title')}</div>
						<div class="isc-teaser__status">{this.t('widget.online')}</div>
					</div>
				</div>
				<p class="isc-teaser__body">{this.engine.text(hasEvent ? 'teaser.bodyEvent' : 'teaser.body')}</p>
				<div class="isc-teaser__actions">
					{this.teaserFlows.map((f) => (
						<button key={f.id} type="button" class="isc-chip isc-chip--small" onClick={() => void this.teaserChoose(f.id)}>
							<span class="isc-chip__icon" aria-hidden="true">{f.icon}</span>
							<span>{this.t(f.titleKey)}</span>
						</button>
					))}
				</div>
			</div>
		);
	}

	renderPanel() {
		const canBack = this.engine.canGoBack && !this.busy;
		return (
			<section class={['isc-panel', this.closing ? 'isc-panel--closing' : '']} role="dialog" aria-modal="false" aria-label={this.t('widget.title')}>
				<header class="isc-header">
					<button type="button" class="isc-iconbtn" aria-label={this.t('widget.back')} disabled={!canBack} onClick={() => this.back()}>
						{backIcon()}
					</button>
					<div class="isc-header__avatar" aria-hidden="true">
						{botIcon()}
						<span class="isc-header__dot" />
					</div>
					<div class="isc-header__text">
						<div class="isc-header__title">{this.t('widget.title')}</div>
						<div class="isc-header__subtitle">{this.t('widget.online')}</div>
					</div>
					<button type="button" class="isc-iconbtn" aria-label={this.t('widget.close')} onClick={() => this.close()}>
						{closeIcon()}
					</button>
				</header>

				<div class="isc-messages" ref="messages" aria-live="polite">
					{this.showHero && (
						<div class="isc-hero isc-fade">
							<div class="isc-hero__avatar" aria-hidden="true">
								{botIcon()}
							</div>
							<div class="isc-hero__title">{this.t('hero.title')}</div>
							<div class="isc-hero__text">{this.t('hero.text')}</div>
						</div>
					)}
					{this.visibleTranscript.map((m, i) => {
						if (i === 0 && this.showHero) {
							return null;
						}
						const prev = this.transcript[i - 1];
						const first = m.role === 'assistant' && (!prev || prev.role !== 'assistant');
						return (
							<div key={i} class={['isc-msg', 'isc-msg--' + m.role, m.kind === 'error' ? 'isc-msg--error' : '', first ? 'isc-msg--first' : '']}>
								{m.role === 'assistant' && (
									<span class="isc-msg__avatar" aria-hidden="true">
										{first && botIcon()}
									</span>
								)}
								<div class="isc-msg__bubble">{m.role === 'assistant' ? linkify(m.text) : m.text}</div>
							</div>
						);
					})}
					{this.isMainMenu && this.renderMainMenu()}
					{!this.isMainMenu && this.renderQuickReplies()}
					{(this.busy || this.revealing) && (
						<div class="isc-msg isc-msg--assistant isc-msg--first" aria-label={this.t('widget.typing')}>
							<span class="isc-msg__avatar" aria-hidden="true">
								{botIcon()}
							</span>
							<div class="isc-msg__bubble isc-typing">
								<span />
								<span />
								<span />
							</div>
						</div>
					)}
				</div>

				<div class="isc-composer">{this.renderPrompt()}</div>

				<footer class="isc-footer">
					<button type="button" class="isc-link" onClick={() => this.restart()} disabled={this.busy}>
						{this.t('widget.restart')}
					</button>
					<span class="isc-footer__privacy">
						<span class="isc-footer__privacy-text">{this.t('widget.privacy')}</span>
						{this.config.privacyUrl && (
							<a class="isc-footer__privacy-link" href={this.config.privacyUrl} target="_blank" rel="noopener noreferrer">
								{this.t('widget.privacyLink')}
							</a>
						)}
					</span>
				</footer>
			</section>
		);
	}

	/**
	 * Quick replies (choice / retry / end) live in the message list, right under
	 * the last assistant bubble, as full-width buttons — same visual language as
	 * the main menu. The composer only hosts text inputs and forms.
	 */
	renderQuickReplies() {
		if (!this.promptReady) {
			return null;
		}
		const p = this.prompt;
		if (p.mode === 'choice') {
			return (
				<div class="isc-replies" role="group">
					{p.options.map((o, i) => (
						<button
							key={o.id}
							type="button"
							class={['isc-reply', 'isc-rise', o.variant === 'primary' ? 'isc-reply--primary' : '']}
							style={stagger(i)}
							onClick={() => this.choose(o)}
						>
							{o.icon && (
								<span class="isc-reply__icon" aria-hidden="true">
									{o.icon}
								</span>
							)}
							<span class="isc-reply__label">{this.engine.text(o.labelKey)}</span>
						</button>
					))}
					{p.allowHuman && (
						<button type="button" class="isc-reply isc-reply--ghost isc-rise" style={stagger(p.options.length)} onClick={() => this.chooseHuman()}>
							<span class="isc-reply__label">{this.t('human.option')}</span>
						</button>
					)}
				</div>
			);
		}
		if (p.mode === 'error') {
			return (
				<div class="isc-replies" role="group">
					<button type="button" class="isc-reply isc-reply--primary isc-rise" onClick={() => this.retry()}>
						<span class="isc-reply__label">{this.t('widget.retry')}</span>
					</button>
					<button type="button" class="isc-reply isc-reply--ghost isc-rise" style={stagger(1)} onClick={() => this.back()}>
						<span class="isc-reply__label">{this.t('widget.back')}</span>
					</button>
				</div>
			);
		}
		if (p.mode === 'end') {
			return (
				<div class="isc-replies" role="group">
					{this.renderReviewCard(p.resolution)}
					<button type="button" class="isc-reply isc-reply--primary isc-rise" style={stagger(1)} onClick={() => this.restart()}>
						<span class="isc-reply__label">{this.t('end.anotherQuestion')}</span>
					</button>
					<button type="button" class="isc-reply isc-reply--ghost isc-rise" style={stagger(2)} onClick={() => this.finish()}>
						<span class="isc-reply__label">{this.t('end.done')}</span>
					</button>
				</div>
			);
		}
		return null;
	}

	/**
	 * Review solicitation (Quicktext "Ďakujeme aj my" template): shown only when the
	 * customer's problem was actually solved by the bot and a review URL is configured.
	 */
	renderReviewCard(resolution: Resolution) {
		if (resolution !== 'resolved' || !this.config.reviewUrl) {
			return null;
		}
		return (
			<div class="isc-review isc-rise" style={stagger(0)}>
				<span class="isc-review__stars" aria-hidden="true">
					★★★★★
				</span>
				<p class="isc-review__text">{this.t('review.text')}</p>
				<a
					class="isc-review__link"
					href={this.config.reviewUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={() => this.analytics.track('review_link_clicked', { flowId: this.state.flowId })}
				>
					{this.t('review.button')}
				</a>
			</div>
		);
	}

	renderPrompt() {
		if (!this.promptReady) {
			return null;
		}
		const p = this.prompt;
		switch (p.mode) {
			case 'choice':
			case 'error':
			case 'end':
				// rendered inside the message list (renderMainMenu / renderQuickReplies)
				return null;
			case 'text':
			case 'email':
				return this.renderTextInput(p);
			case 'form':
				return (
					<form
						class="isc-form isc-rise"
						onSubmit={(e: Event) => {
							e.preventDefault();
							this.submitForm();
						}}
					>
						{p.fields.map((f, i) => (
							<label key={f.name} class="isc-field">
								<span class="isc-field__label">
									{this.t(f.labelKey)}
									{f.required && <span aria-hidden="true"> *</span>}
								</span>
								<input
									ref={i === 0 ? 'input' : undefined}
									class={['isc-input', this.formErrors[f.name] ? 'isc-input--error' : '']}
									type={f.type ?? 'text'}
									maxlength={f.maxLength ?? 200}
									autocomplete="off"
									value={this.formValues[f.name] ?? ''}
									required={!!f.required}
									onInput={(e: Event) => (this.formValues[f.name] = (e.target as HTMLInputElement).value)}
								/>
								{this.formErrors[f.name] && <span class="isc-field__error">{this.formErrors[f.name]}</span>}
							</label>
						))}
						<button type="submit" class="isc-btn isc-btn--primary">
							{this.t('widget.send')}
						</button>
					</form>
				);
			case 'confirmation':
				return (
					<div class="isc-confirm isc-rise">
						<dl class="isc-summary">
							{p.summary.map((row) => (
								<div key={row.labelKey} class="isc-summary__row">
									<dt>{this.t(row.labelKey)}</dt>
									<dd>{row.value}</dd>
								</div>
							))}
						</dl>
						<button type="button" class="isc-btn isc-btn--primary" onClick={() => this.confirm()}>
							{this.t('common.confirm')}
						</button>
					</div>
				);
			default:
				return null;
		}
	}

	renderTextInput(p: Extract<Prompt, { mode: 'text' } | { mode: 'email' }>) {
		const isEmail = p.mode === 'email';
		const multiline = !isEmail && p.multiline;
		const maxLength = isEmail ? 254 : p.maxLength;
		const placeholder = isEmail ? 'meno@example.com' : p.placeholderKey ? this.t(p.placeholderKey) : '';
		const required = isEmail ? true : p.required;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Enter' && !e.shiftKey && !multiline) {
				e.preventDefault();
				this.submitText();
			}
			if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && multiline) {
				e.preventDefault();
				this.submitText();
			}
		};
		return (
			<form
				class={['isc-inputrow', 'isc-rise', multiline ? 'isc-inputrow--multiline' : '']}
				onSubmit={(e: Event) => {
					e.preventDefault();
					this.submitText();
				}}
			>
				{multiline ? (
					<textarea
						ref="input"
						class={['isc-input', this.inputError ? 'isc-input--error' : '']}
						rows={3}
						maxlength={maxLength}
						placeholder={placeholder}
						value={this.inputValue}
						onInput={(e: Event) => (this.inputValue = (e.target as HTMLTextAreaElement).value)}
						onKeydown={onKey}
					/>
				) : (
					<input
						ref="input"
						class={['isc-input', this.inputError ? 'isc-input--error' : '']}
						type={isEmail ? 'email' : 'text'}
						inputmode={isEmail ? 'email' : undefined}
						autocomplete={isEmail ? 'email' : 'off'}
						maxlength={maxLength}
						placeholder={placeholder}
						value={this.inputValue}
						onInput={(e: Event) => (this.inputValue = (e.target as HTMLInputElement).value)}
						onKeydown={onKey}
					/>
				)}
				<div class="isc-inputrow__actions">
					{!required && (
						<button type="button" class="isc-link" onClick={() => this.skipText()}>
							{this.t('widget.skip')}
						</button>
					)}
					<button type="submit" class="isc-btn isc-btn--primary" aria-label={this.t('widget.send')}>
						{sendIcon()}
					</button>
				</div>
				{this.inputError && (
					<div class="isc-inputrow__error" role="alert">
						{this.inputError}
					</div>
				)}
			</form>
		);
	}
}

/** Owner decision 2026-09-17: the greeting bubble appears 4 s after the page loaded. */
const TEASER_DELAY_MS = 4000;
/** Owner decision 2026-09-18: the teaser folds back into the launcher after 4 s unless the customer is hovering it. */
const TEASER_AUTO_HIDE_MS = 4000;
const TEASER_HIDE_ANIM_MS = 200;
const TEASER_KEY = 'inv-sc-teaser-dismissed';
/** Phones and tablets; keep in sync with the max-width: 991.98px block in support-chat.css. */
const COMPACT_QUERY = '(max-width: 991.98px)';
/** Pages mark a ticket form the launcher must not float over, and where the inline help link goes. */
const AVOID_SELECTOR = '[data-isc-avoid]';
const HELP_SLOT_SELECTOR = '[data-isc-help]';
/** What counts as a tap target inside a marked form (labels carry the drawn checkbox / radio). */
const AVOID_CONTROLS = 'button, a, input, select, textarea, label';
const AVOID_GAP_PX = 8;
const PANEL_CLOSE_MS = 180;
/** Typing pause before an assistant bubble appears: base + per character, capped. */
// Pacing of assistant replies (owner feedback: instant bubbles feel chaotic). Each reply
// "types" for a length-based moment, and consecutive replies get a short reading pause
// between them, so a 3-bubble answer lands one thought at a time.
const REVEAL_BASE_MS = 300;
const REVEAL_PER_CHAR_MS = 9;
const REVEAL_MAX_MS = 1000;
const REVEAL_GAP_MS = 350;
const STAGGER_MS = 80;
/** Rows revealed by a tap of the customer's own - tighter, so the group is complete in well under half a second. */
const EXPAND_STAGGER_MS = 40;

function stagger(index: number, step = STAGGER_MS) {
	return { animationDelay: index * step + 'ms' };
}

function prefersReducedMotion(): boolean {
	try {
		return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
	} catch {
		return false;
	}
}

function isCompactViewport(): boolean {
	try {
		return typeof matchMedia === 'function' && matchMedia(COMPACT_QUERY).matches;
	} catch {
		return false;
	}
}

function isTeaserDismissed(): boolean {
	try {
		return sessionStorage.getItem(TEASER_KEY) === '1';
	} catch {
		return false;
	}
}

function markTeaserDismissed() {
	try {
		sessionStorage.setItem(TEASER_KEY, '1');
	} catch {
		/* ignore */
	}
}

/**
 * Links inside an assistant bubble ("tickets are still on sale here: https://…",
 * the organizer's address). Bubbles are rendered as TEXT NODES, never as HTML, so
 * this only ever turns a recognised http(s) address or e-mail into an anchor and
 * leaves everything else as plain text - there is no way for a server value or a
 * customer's own words to become markup.
 */
const LINK_PATTERN = /(https?:\/\/[^\s<>"')\]]+|[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,})/g;
/** A sentence ends with a full stop or a comma - those do not belong to the link. */
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

export function linkify(text: string): (string | VNode)[] | string {
	if (!text || (!text.includes('://') && !text.includes('@'))) {
		return text;
	}

	const parts = text.split(LINK_PATTERN);
	if (parts.length < 2) {
		return text;
	}

	const out: (string | VNode)[] = [];
	for (let i = 0; i < parts.length; i++) {
		const part = parts[i];
		if (!part) {
			continue;
		}
		// odd indexes are the captured matches
		if (i % 2 === 0 || !LINK_PATTERN.test(part)) {
			out.push(part);
			LINK_PATTERN.lastIndex = 0;
			continue;
		}
		LINK_PATTERN.lastIndex = 0;
		const trailing = TRAILING_PUNCTUATION.exec(part);
		const link = trailing ? part.slice(0, part.length - trailing[0].length) : part;
		const href = link.includes('://') ? link : 'mailto:' + link;
		out.push(
			<a key={i} class="isc-msg__link" href={href} target="_blank" rel="noopener noreferrer">
				{link}
			</a>,
		);
		if (trailing) {
			out.push(trailing[0]);
		}
	}
	return out;
}

function stripInternal(answers: Record<string, unknown>): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const [k, v] of Object.entries(answers)) {
		if (k === 'actionToken' || k === 'escalationKey' || k === 'emailCode' || k === 'organizerEmail' || k === 'organizerPhone') {
			continue;
		}
		out[k] = v;
	}
	return out;
}

function randomKey(): string {
	try {
		const c = globalThis.crypto;
		if (c && 'randomUUID' in c) {
			return c.randomUUID();
		}
		const arr = new Uint8Array(16);
		c!.getRandomValues(arr);
		return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
	} catch {
		return 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2);
	}
}

const chevronIcon = () => (
	<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
		<path fill="currentColor" d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6z" />
	</svg>
);
const closeIcon = () => (
	<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
		<path fill="currentColor" d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.71 2.88 18.3 9.17 12 2.88 5.71 4.29 4.29l6.3 6.3 6.3-6.3z" />
	</svg>
);
const backIcon = () => (
	<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
		<path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20z" />
	</svg>
);
const sendIcon = () => (
	<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
		<path fill="currentColor" d="M2 21l21-9L2 3v7l15 2-15 2z" />
	</svg>
);
const botIcon = () => (
	<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
		<path
			fill="currentColor"
			d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h3a4 4 0 0 1 4 4v5a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-5a4 4 0 0 1 4-4h3V5.73A2 2 0 0 1 12 2zm-3 9.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm6 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM9 17h6v-1.5H9z"
		/>
	</svg>
);

export default toNative(SupportChatWidget);
